import React from 'react';
import { Link } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from './ui/sheet';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';

export default function CartDrawer() {
    const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal, lineKey } = useCart();
    const { format } = useCurrency();

    return (
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent side="right" className="bg-white w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
                <SheetTitle className="sr-only">Your shopping bag</SheetTitle>
                <div className="px-6 py-5 flex items-center justify-between border-b border-[rgba(90,36,50,0.10)]">
                    <div className="flex items-center gap-3">
                        <ShoppingBag className="w-5 h-5" style={{ color: 'var(--brand-burgundy)' }} />
                        <h3 className="font-serif text-xl" style={{ color: 'var(--brand-burgundy)' }}>Your Bag</h3>
                    </div>
                    <button onClick={() => setDrawerOpen(false)} aria-label="Close" className="p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="cart-empty-state">
                            <ShoppingBag className="w-10 h-10 mb-4" style={{ color: 'rgba(90,36,50,0.35)' }} />
                            <p className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>Your bag is quiet.</p>
                            <p className="text-sm mt-2" style={{ color: 'var(--ink-muted)' }}>Add a few pieces to begin.</p>
                            <Link to="/shop" onClick={() => setDrawerOpen(false)} className="mt-6 btn-outline-burgundy px-5 py-2.5 rounded-xl text-xs tracking-[0.18em] uppercase">
                                Shop All
                            </Link>
                        </div>
                    ) : (
                        <ul className="space-y-5">
                            {items.map((it) => {
                                const key = lineKey(it);
                                return (
                                    <li key={key} className="flex gap-4" data-testid={`cart-line-item-${it.product_slug}`}>
                                        <div className="w-20 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-[var(--brand-cream)]">
                                            {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-3">
                                                <Link to={`/product/${it.product_slug}`} onClick={() => setDrawerOpen(false)} className="font-serif text-base hover:underline" style={{ color: 'var(--brand-burgundy)' }}>
                                                    {it.name}
                                                </Link>
                                                <button onClick={() => removeItem(key)} aria-label="Remove" className="p-1 hover:opacity-70">
                                                    <Trash2 className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                                                </button>
                                            </div>
                                            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                                                {it.size && <span>{it.size}</span>}{it.size && it.color && <span> · </span>}{it.color}
                                            </p>
                                            {it.special_request && <p className="text-[11px] mt-0.5 italic" style={{ color: 'var(--brand-burgundy)' }}>Note: {it.special_request}</p>}
                                            <div className="mt-2 flex items-center justify-between">
                                                <div className="flex items-center border border-[rgba(90,36,50,0.18)] rounded-lg">
                                                    <button onClick={() => updateQty(key, it.quantity - 1)} className="p-1.5" aria-label="Decrease">
                                                        <Minus className="w-3.5 h-3.5" />
                                                    </button>
                                                    <span className="px-3 text-sm" data-testid={`cart-line-qty-${it.product_slug}`}>{it.quantity}</span>
                                                    <button onClick={() => updateQty(key, it.quantity + 1)} className="p-1.5" aria-label="Increase">
                                                        <Plus className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                                <p className="text-sm" style={{ color: 'var(--ink)' }}>{format(it.price * it.quantity)}</p>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="px-6 py-5 border-t border-[rgba(90,36,50,0.10)] bg-[var(--brand-cream)]">
                        <div className="flex items-center justify-between mb-4">
                            <span className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Subtotal</span>
                            <span className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }} data-testid="cart-subtotal">{format(subtotal)}</span>
                        </div>
                        <Link to="/cart" onClick={() => setDrawerOpen(false)} data-testid="cart-checkout-link" className="btn-burgundy w-full text-center block px-5 py-3 rounded-xl text-xs tracking-[0.18em] uppercase font-medium">
                            Review & Checkout
                        </Link>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
